import { useCallback, useMemo, useState } from "react";

import Link from "next/link";

import { DownloadOutlined, EyeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { IFilter } from "@gsmvalve/gsmvalve-table";
import { t, Trans } from "@lingui/macro";
import { Button, Image, Tag } from "antd";
import { keyBy } from "lodash";
import { formStateLabel } from "pages/forms/[id]";

import AppLayout from "layout/app";

import EmptyStates from "components/empty-states";
import DownloadModal from "components/modals/download-modal";
import Table, { mapOptionsToFilters, onHasFilters } from "components/table";

import { useApplicationContext } from "contexts/app";

import { DATA_TYPE, FILTER_CONDITION, ORDERING, Record } from "@gsmvalve/gsmvalve-table/dist/cjs/types";
import { Form, FormResponse } from "types";

import useOptions from "hooks/options";

import { getFormResponseExport } from "utils/navirec-forms";
import { extractUuidFromURL } from "utils/uuid-utils";

const filters = (basePath: string | undefined, account: string, client: any): IFilter[] => [
  {
    key: "vehicle",
    title: t`Vehicles`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}vehicles/?account=${account}&active=true`,
      label: t`Search vehicles`,
      mode: "multiple",
      valueKey: "id",
      labelKey: "name_display",
      client: client,
    },
  },
  {
    key: "driver",
    title: t`Drivers`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}drivers/?account=${account}&active=true`,
      label: t`Search drivers`,
      mode: "multiple",
      valueKey: "id",
      labelKey: "name",
      client: client,
    },
  },
  {
    key: "created_by",
    title: t`Created by`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}users/?account=${account}`,
      label: t`Search created by`,
      mode: "multiple",
      valueKey: "id",
      labelKey: "name_display",
      client: client,
    },
  },
  {
    key: "form",
    title: t`Forms`,
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.IN,
    autoComplete: {
      url: `${basePath}forms/?account=${account}&state=active`,
      label: t`Search forms`,
      mode: "multiple",
      valueKey: "id",
      labelKey: "name",
      client: client,
    },
  },
  {
    key: "state",
    title: t`State`,
    options: [
      { label: t`Submitted`, value: "submitted" },
      { label: t`Draft`, value: "draft" },
    ],
    type: DATA_TYPE.CHOICE,
    condition: FILTER_CONDITION.EQUALS,
  },
];

const modifier = (data: any) => {
  const records: Record[] = [];

  data?.forEach((rows: any) => {
    const row = rows?.data;

    const usersById = keyBy(rows?.data.users, "url");
    const driversById = keyBy(rows?.data.drivers, "url");
    const vehiclesById = keyBy(rows?.data.vehicles, "url");
    const formsById = keyBy(rows?.data.forms, "url");

    row?.form_responses?.forEach((form: any) => {
      records.push({
        ...form,
        created_by: usersById?.[form.created_by]?.name_display || "-",
        vehicleName: vehiclesById?.[form.vehicle]?.name_display || "-",
        driverName: driversById?.[form.driver]?.name || "-",
        form: formsById?.[form.form]?.name || "-",
        formId: extractUuidFromURL(form.form),
      });
    });
  });

  return records;
};

const Responses = () => {
  const { httpClient, session, language } = useApplicationContext();

  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();

  const manageColumns = useCallback(() => {
    return [
      {
        title: t`Action`,
        fixed: true,
        isVisible: true,
        key: "action",
        width: 170,
        render(data: any) {
          return (
            <div>
              <div>
                <Link
                  target="_blank"
                  href={`/forms/${extractUuidFromURL(data.formId)}/responses/${data.id}`}
                  style={{ zIndex: 1 }}
                >
                  <EyeOutlined /> <Trans>View response</Trans>
                </Link>
              </div>
              <div style={{ marginTop: 4 }}>
                <a
                  onClick={() =>
                    getFormResponseExport(data.id as string, language, httpClient, 'pdf').then((res) => setDownloadUrl(res))
                  }
                  style={{ zIndex: 1, marginRight: 8 }}
                >
                  <DownloadOutlined /> <Trans>PDF</Trans>
                </a>
                <a
                  onClick={() =>
                    getFormResponseExport(data.id as string, language, httpClient, 'xlsx').then((res) => setDownloadUrl(res))
                  }
                  style={{ zIndex: 1 }}
                >
                  <DownloadOutlined /> <Trans>Excel</Trans>
                </a>
              </div>
            </div>
          );
        },
      },
      {
        title: t`Form`,
        isVisible: true,
        key: "form",
        width: 170,
        render(data: FormResponse) {
          return data.form;
        },
      },
      { key: "account", title: t`Account` },
      { key: "description", title: t`Description` },
      { key: "name", title: t`Name` },
      { key: "responses_count", title: t`Responses count` },
      {
        title: t`Created by`,
        isVisible: true,
        isSortable: false,
        dataIndex: ["created_by"],
        key: "created_by",
        render(data: FormResponse) {
          return data.created_by;
        },
      },
      {
        title: t`State`,
        isVisible: true,
        isSortable: true,
        dataIndex: ["state"],
        key: "state",
        render(data: FormResponse) {
          return (
            <Tag color={data.state === "submitted" ? "green" : undefined}>
              {data?.state ? formStateLabel[data.state] : data?.state}
            </Tag>
          );
        },
      },
      { key: "updated_at", title: t`Updated at` },
      {
        title: t`Created at`,
        isVisible: true,
        isSortable: true,
        key: "created_at",
        dataIndex: ["created_at"],
        type: DATA_TYPE.DATETIME,
      },

      {
        title: t`Vehicle`,
        isVisible: true,
        isSortable: false,
        key: "vehicle",
        dataIndex: ["vehicleName"],
        render(data: any) {
          if (!data.vehicle && !data.vehicleName) {
            return "-";
          }

          return data.vehicleName;
        },
      },
      {
        title: t`Driver`,
        isVisible: true,
        isSortable: false,
        key: "driver",
        dataIndex: ["driverName"],
        render(data: any) {
          if (!data.driver && !data.driverName) {
            return "-";
          }

          return data.driverName;
        },
      },
      { key: "url", omit: true },
      { key: "form", omit: true },
    ];
  }, []);

  const url = `form_responses/?sideload=users,vehicles,drivers,forms&account=${session?.accountId}`;
  const columns = manageColumns();

  const { result: options } = useOptions(`form_responses/`);

  const mappedFilters = useMemo(
    () => mapOptionsToFilters(options, filters(httpClient.basePath, String(session?.accountId), httpClient.swrFetcher)),
    [httpClient, options, session]
  );

  return (
    <AppLayout fullscreen pageTitle={t`Forms responses`}>
      <Table<Form>
        key="responses"
        controls={{ manageColumn: true, filterBtn: false, quickFilter: true }}
        id={`${session?.accountId}-form-responses`}
        columns={{
          filtersPath: "/form_responses/",
          path: "Form",
          customColumns: columns,
          customFilters: mappedFilters,
          quickFilters: [
            { key: "form", placeholder: t`Forms`, span: 5 },
            { key: "vehicle", placeholder: t`Vehicle`, span: 5 },
            { key: "driver", placeholder: t`Driver`, span: 5 },
            { key: "created_by", placeholder: t`Created by`, span: 5 },
          ],
        }}
        records={{
          url: url,
          limit: 100,
          sort: [{ key: "created_at", order: "desc" as ORDERING }],

          modifier: modifier,
        }}
        empty={{
          onHasFilter: onHasFilters(),
          emptyWithoutFilter: (
            <EmptyStates
              title={t`No responses yet`}
              description={t`Responses will appear here once you create a form and share it.`}
              icon={<Image src={"/images/responses-empty-state.svg"} width={260} height={260} />}
              actions={[
                <Button href="/forms/new/" type={"primary"} size={"middle"} icon={<PlusCircleOutlined />}>
                  <span>
                    <Trans>New form</Trans>
                  </span>
                </Button>,
              ]}
            />
          ),
        }}
        offset={160}
      />
      <DownloadModal installUrl={downloadUrl} onClose={() => setDownloadUrl(undefined)} title="Download file" />
    </AppLayout>
  );
};

export default Responses;
